// Les gestes qui font qu'une application se sent tenue dans la main.
//
// Koinonia exécutait. Elle ne répondait pas. On tapait, il se passait quelque
// chose — mais rien entre les deux : pas d'accusé de réception sous le doigt,
// pas de résistance, pas d'inertie. C'est ce qui fait qu'une application web
// « sent le site » alors qu'une application native sent l'objet.
//
// Trois manques, par ordre de ce qui se remarque :
//
//   1. TIRER POUR RAFRAÎCHIR — le geste que tout le monde connaît. Son absence
//      se remarque parce qu'on l'essaie sans y penser, et qu'il ne se passe rien.
//   2. LE RETOUR AU TOUCHER — un élément qui ne bouge pas quand on appuie
//      laisse croire, pendant la fraction de seconde qui suit, qu'on a raté
//      sa cible.
//   3. L'ARRIVÉE DES ÉCRANS — un écran qui apparaît d'un coup n'a pas de
//      provenance. Un écran qui glisse vient de quelque part.

/**
 * Y a-t-il un doigt en face ?
 *
 * ⚠️ `'ontouchstart' in window` NE SUFFIT PAS. Certains navigateurs tactiles ne
 * l'exposent pas — mesuré : dans un Chrome en émulation mobile, il vaut `false`
 * alors que `maxTouchPoints` vaut 5 et que le pointeur est grossier. S'y fier
 * seul, c'est priver de gestes des appareils qui les acceptent parfaitement.
 * On croise donc trois signaux, et un seul suffit.
 */
const auDoigt = () =>
  "ontouchstart" in window ||
  navigator.maxTouchPoints > 0 ||
  matchMedia("(pointer: coarse)").matches;

/* ══════════════════════════════════════════════ tirer pour rafraîchir */

const SEUIL = 72;        // distance à franchir pour déclencher
const PLAFOND = 110;     // au-delà, le doigt tire dans le vide
const RESISTANCE = 0.55; // le contenu suit le doigt à moitié : c'est ce qui
                         // donne la sensation d'élastique plutôt que de tiroir

/* ⚠️ LA MARGE D'IMMOBILITÉ — la valeur la plus importante de ce fichier.
   Un appui n'est JAMAIS immobile : le doigt roule de quelques pixels entre le
   contact et le relâchement. Tant qu'on n'a pas franchi cette marge, on ne
   touche à rien — et surtout on n'appelle pas `preventDefault()`, qui annule
   le clic que le navigateur allait émettre.
   Sans elle, un appui un peu appuyé était pris pour un début de traction :
   le geste s'engageait, le clic était supprimé, et RIEN NE S'OUVRAIT.
   24 px : plus qu'un doigt qui tremble, moins qu'un doigt qui tire. */
const MARGE = 24;

/**
 * Installe le geste sur la fenêtre.
 *
 * `quoiRafraichir` rend une promesse. Tant qu'elle n'est pas tenue, l'anneau
 * tourne — on ne triche pas avec un délai fixe : un rafraîchissement qui
 * paraît fini avant de l'être est pire que pas de rafraîchissement du tout.
 */
export function tirerPourRafraichir(quoiRafraichir) {
  if (!auDoigt()) return () => {};

  const zone = document.createElement("div");
  zone.className = "tirer";
  zone.innerHTML = `<span class="tirer__anneau"></span>`;
  document.body.appendChild(zone);

  let departY = 0;
  let departX = 0;
  let distance = 0;
  let actif = false;    // un doigt est posé, on observe
  let engage = false;   // la traction est reconnue, on prend la main
  let enCours = false;

  const placer = (d, anime = false) => {
    zone.style.transition = anime ? "transform .22s cubic-bezier(.2,.8,.3,1), opacity .22s" : "";
    zone.style.transform = `translateY(${d}px)`;
    zone.style.opacity = String(Math.min(1, d / SEUIL));
    // L'anneau tourne AVEC le doigt avant de tourner tout seul : le geste et
    // l'indicateur ne font qu'un tant qu'on n'a pas lâché.
    zone.firstElementChild.style.transform = `rotate(${d * 3}deg)`;
    zone.classList.toggle("est-prete", d >= SEUIL);
  };

  const surDebut = (e) => {
    if (enCours || window.scrollY > 0 || e.touches.length !== 1) return;
    departY = e.touches[0].clientY;
    departX = e.touches[0].clientX;
    actif = true;
    engage = false;
    distance = 0;
  };

  const surMouvement = (e) => {
    if (!actif) return;
    const brut = e.touches[0].clientY - departY;
    const lateral = Math.abs(e.touches[0].clientX - departX);

    // Un geste vers le haut, ou une page qu'on a fait défiler entre-temps :
    // on rend la main au défilement natif sans rien avoir perturbé.
    if (brut <= 0 || window.scrollY > 0) {
      if (distance) placer((distance = 0), true);
      actif = false;
      engage = false;
      return;
    }

    if (!engage) {
      // Toujours dans la marge : c'est peut-être un appui. On ne fait RIEN,
      // et surtout on laisse le clic vivre sa vie.
      if (brut < MARGE) return;
      // Franchie, mais en biais : c'est un balayage horizontal, pas une
      // traction. On se retire plutôt que de lui voler son geste.
      if (lateral > brut * 0.8) {
        actif = false;
        return;
      }
      engage = true;
      // On repart de la marge, sinon l'anneau saute de 24 px d'un coup.
      departY += MARGE;
    }

    // La résistance croît avec la distance : les premiers pixels suivent le
    // doigt, les derniers résistent. Sans cela le geste part trop vite et on
    // dépasse le seuil sans l'avoir voulu.
    distance = Math.min(PLAFOND, (e.touches[0].clientY - departY) * RESISTANCE);
    // On ne prend la main sur le défilement qu'une fois ENGAGÉ — jamais avant.
    if (e.cancelable) e.preventDefault();
    placer(distance);
  };

  const surFin = async () => {
    if (!actif) return;
    actif = false;
    // Jamais engagé : c'était un appui, pas une traction. On n'a touché à rien.
    if (!engage) return;
    engage = false;
    if (distance < SEUIL) {
      placer(0, true);
      return;
    }
    enCours = true;
    zone.classList.add("est-en-cours");
    placer(SEUIL, true);
    try {
      await quoiRafraichir();
    } catch {
      // Un rafraîchissement qui échoue ne doit pas laisser l'anneau tourner
      // pour l'éternité : on referme, l'écran garde ce qu'il avait.
    }
    // Un battement de cœur avant de refermer. Sans lui, un rafraîchissement
    // instantané ne se voit pas, et le membre croit que rien ne s'est passé.
    await new Promise((r) => setTimeout(r, 260));
    zone.classList.remove("est-en-cours");
    placer((distance = 0), true);
    enCours = false;
  };

  // `passive: false` sur le mouvement, parce qu'on doit pouvoir l'empêcher.
  window.addEventListener("touchstart", surDebut, { passive: true });
  window.addEventListener("touchmove", surMouvement, { passive: false });
  window.addEventListener("touchend", surFin, { passive: true });
  window.addEventListener("touchcancel", surFin, { passive: true });

  return () => {
    window.removeEventListener("touchstart", surDebut);
    window.removeEventListener("touchmove", surMouvement);
    window.removeEventListener("touchend", surFin);
    window.removeEventListener("touchcancel", surFin);
    zone.remove();
  };
}

/* ══════════════════════════════════════════════════ retour au toucher */

/**
 * Marque l'élément sous le doigt, le temps de l'appui.
 *
 * Fait en JavaScript et non avec `:active` : sur Android, `:active` ne
 * s'applique qu'après un délai destiné à distinguer l'appui du défilement —
 * si bien que l'effet arrive quand on a déjà relâché, ou pas du tout.
 *
 * Un seul écouteur pour toute l'application, posé sur la racine : les écrans
 * se redessinent sans arrêt, et attacher quoi que ce soit à chaque bouton
 * serait à refaire à chaque rendu.
 */
export function retourAuToucher() {
  if (!auDoigt()) return;
  const CIBLES = "button, a, .post__action, .convo, .membre, .story, .onglet, [role='button']";
  let presse = null;

  const relacher = () => {
    presse?.classList.remove("est-presse");
    presse = null;
  };

  document.addEventListener(
    "touchstart",
    (e) => {
      const el = e.target.closest(CIBLES);
      if (!el || el.disabled) return;
      presse = el;
      el.classList.add("est-presse");
    },
    { passive: true }
  );

  // On relâche aussi au MOUVEMENT : sans cela, un doigt qui part en défilement
  // depuis un bouton laisse celui-ci enfoncé pendant tout le geste.
  document.addEventListener("touchmove", relacher, { passive: true });
  document.addEventListener("touchend", relacher, { passive: true });
  document.addEventListener("touchcancel", relacher, { passive: true });
}
